<template>
    <div class="container">
        <schoolAndDate :schoolName="schoolName" :dateTime="dateTime" />
        <div class="wrap" ref="viewport">
            <transition name="page-turn-fade" mode="out-in">
                <div class="meal-list-wrap" :key="currentPageKey">
                    <div class="meal-list" v-for="(section, sectionIndex) in currentPage.sections" :key="section.sectionKey + '-' + sectionIndex">
                        <div class="meal-block">
                            <div class="meal-item meal-head">
                                <div class="meal-title col-week"><span>星期</span></div>
                                <div class="meal-title col-rice"><span>米饭</span></div>
                                <div class="meal-title col-meat"><span>荤菜1</span></div>
                                <div class="meal-title col-meat"><span>荤菜2</span></div>
                                <div class="meal-title col-veg"><span>蔬菜</span></div>
                            </div>
                            <div class="meal-item" v-for="row in section.rows" :key="row.rowKey">
                                <div class="meal-content col-week">
                                    <span class="week-date">{{ row.date && row.date.length >= 10 && row.date.indexOf('-') === 4 ? row.date.slice(5) : row.date }}</span>
                                    <span class="week-label">{{ row.week }}</span>
                                </div>
                                <div class="meal-content col-rice"><span>{{ row.rice }}</span></div>
                                <div class="meal-content col-meat"><span>{{ row.meat1 }}</span></div>
                                <div class="meal-content col-meat"><span>{{ row.meat2 }}</span></div>
                                <div class="meal-content col-veg"><span>{{ row.vegetable }}</span></div>
                            </div>
                        </div>
                    </div>
                </div>
            </transition>

            <div class="measure-layer" aria-hidden="true">
                <div class="meal-list-wrap measure-wrap">
                    <div class="meal-list" v-for="(section, sectionIndex) in displaySections" :key="section.sectionKey + '-measure'">
                        <div class="meal-block">
                            <div class="meal-item meal-head" :ref="'measureHead-' + sectionIndex">
                                <div class="meal-title col-week"><span>星期</span></div>
                                <div class="meal-title col-rice"><span>米饭</span></div>
                                <div class="meal-title col-meat"><span>荤菜1</span></div>
                                <div class="meal-title col-meat"><span>荤菜2</span></div>
                                <div class="meal-title col-veg"><span>蔬菜</span></div>
                            </div>
                            <div class="meal-item" v-for="(row, rowIndex) in section.rows" :key="row.rowKey" :ref="'measureRow-' + sectionIndex + '-' + rowIndex">
                                <div class="meal-content col-week">
                                    <span class="week-date">{{ row.date && row.date.length >= 10 && row.date.indexOf('-') === 4 ? row.date.slice(5) : row.date }}</span>
                                    <span class="week-label">{{ row.week }}</span>
                                </div>
                                <div class="meal-content col-rice"><span>{{ row.rice }}</span></div>
                                <div class="meal-content col-meat"><span>{{ row.meat1 }}</span></div>
                                <div class="meal-content col-meat"><span>{{ row.meat2 }}</span></div>
                                <div class="meal-content col-veg"><span>{{ row.vegetable }}</span></div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <PageTurnControl class="page-turn-control-position" :current="currentPageIndex + 1" :total="pages.length" theme="meal" @prev="goPrevPage" @next="goNextPage" />
        </div>
    </div>
</template>

<script>
import { paginateMeasuredSections } from '../assets/js/contentPaging';
import { canAutoTurnPage, getNextPageIndex, getPrevPageIndex } from '../assets/js/pageTurn';
import { buildNutritiousDiningMenuRows } from '../assets/js/nutritiousDiningMenuRows';
import PageTurnControl from './PageTurnControl.vue';
import schoolAndDate from './schoolAndDate.vue';

var MAX_LINES = 3;
var AUTO_FIT_LINE_HEIGHT = 1.15;
// Use per-cell baseline font-size as the ceiling.
// Only shrink when needed; do NOT unify all cells to one font-size.
var AUTO_FIT_MIN_FONT_SIZE = 10;

export default {
    props: {
        deptId: {
            type: String,
            default: '',
        },
        outletsId: {
            type: String,
            default: '',
        },
        dateTime: {
            type: String,
            default: '',
        },
        schoolName: {
            type: String,
            default: '',
        },
    },
    components: { PageTurnControl, schoolAndDate },
    data() {
        return {
            menuInfo: {},
            pages: [],
            currentPageIndex: 0,
            pageTimer: null,
            autoFitCache: Object.create(null),
        };
    },
    computed: {
        displayRows() {
            return buildNutritiousDiningMenuRows(this.menuInfo);
        },
        displaySections() {
            var rows = this.displayRows.map(function (row, index) {
                return {
                    rowKey: 'menu-row-' + index,
                    date: row && row.date ? row.date : '',
                    week: row && row.week ? row.week : '',
                    rice: row && row.rice ? row.rice : '',
                    meat1: row && row.meat1 ? row.meat1 : '',
                    meat2: row && row.meat2 ? row.meat2 : '',
                    vegetable: row && row.vegetable ? row.vegetable : '',
                };
            });

            return [
                {
                    sectionKey: 'menu-week',
                    sectionHeader: { startDate: '', endDate: '' },
                    rows: rows,
                },
            ];
        },
        currentPage() {
            return this.pages[this.currentPageIndex] || { sections: [] };
        },
        currentPageKey() {
            return 'page-' + this.currentPageIndex;
        },
        autoplayDuration() {
            var query = this.$route && this.$route.query ? this.$route.query : {};
            var autoplay = query && query.autoplay ? parseInt(query.autoplay, 10) : 0;

            if (isNaN(autoplay) || autoplay <= 0) {
                return 0;
            }

            return autoplay;
        },
    },
    watch: {
        menuInfo: {
            deep: true,
            handler: function () {
                this.rebuildPages();
            },
        },
        '$route.query.autoplay': function () {
            this.startPageTimer();
        },
    },
    mounted() {
        this.fetchMenuInfo();
        window.addEventListener('resize', this.rebuildPages);
    },
    beforeDestroy() {
        window.removeEventListener('resize', this.rebuildPages);
        this.stopPageTimer();
    },
    methods: {
        fetchMenuInfo() {
            var param = {
                canteenId: this.deptId,
            };
            this.$get(this.$api.getNutritiousDiningMenuScreenInfo, param).then((res) => {
                this.menuInfo = res && res.data && typeof res.data === 'object' ? res.data : {};
            });
        },
        stopPageTimer() {
            if (this.pageTimer) {
                clearInterval(this.pageTimer);
                this.pageTimer = null;
            }
        },
        startPageTimer() {
            var vm = this;
            this.stopPageTimer();
            if (!canAutoTurnPage(this.pages.length, this.autoplayDuration)) {
                return;
            }
            this.pageTimer = setInterval(function () {
                vm.currentPageIndex = getNextPageIndex(vm.currentPageIndex, vm.pages.length);
            }, this.autoplayDuration);
        },
        restartPageTimer() {
            this.stopPageTimer();
            this.startPageTimer();
        },
        goPrevPage() {
            this.currentPageIndex = getPrevPageIndex(this.currentPageIndex, this.pages.length);
            this.restartPageTimer();
            this.$nextTick(function () {
                this.applyAutoFit();
            });
        },
        goNextPage() {
            this.currentPageIndex = getNextPageIndex(this.currentPageIndex, this.pages.length);
            this.restartPageTimer();
            this.$nextTick(function () {
                this.applyAutoFit();
            });
        },
        getRefElement(refName) {
            var refValue = this.$refs[refName];
            return Array.isArray(refValue) ? refValue[0] : refValue;
        },
        getMeasuredSection(section, sectionIndex) {
            var headEl = this.getRefElement('measureHead-' + sectionIndex);
            var rows = section.rows.map(function (row, rowIndex) {
                var rowEl = this.getRefElement('measureRow-' + sectionIndex + '-' + rowIndex);
                return {
                    rowKey: row.rowKey,
                    measuredHeight: rowEl ? rowEl.offsetHeight : 0,
                    payload: row,
                };
            }, this);

            return {
                sectionKey: section.sectionKey,
                sectionHeader: section.sectionHeader,
                headerHeight: headEl ? headEl.offsetHeight : 0,
                rows: rows,
            };
        },
        getMeasureWrapInset() {
            var measureWrap = this.$el ? this.$el.querySelector('.measure-wrap') : null;
            var styles;
            if (!measureWrap) {
                return 0;
            }
            styles = window.getComputedStyle(measureWrap);
            return (parseFloat(styles.paddingTop) || 0) + (parseFloat(styles.paddingBottom) || 0);
        },
        getSectionGap() {
            var firstSection = this.$el ? this.$el.querySelector('.measure-wrap .meal-list') : null;
            var styles;
            if (!firstSection) {
                return 0;
            }
            styles = window.getComputedStyle(firstSection);
            return parseFloat(styles.marginBottom) || 0;
        },
        rebuildPages() {
            this.$nextTick(function () {
                var viewport = this.$refs.viewport;
                var pageInset = this.getMeasureWrapInset();
                var sectionGap = this.getSectionGap();
                var measuredSections = this.displaySections.map(function (section, index) {
                    return this.getMeasuredSection(section, index);
                }, this);

                this.pages = paginateMeasuredSections({
                    maxPageHeight: viewport ? viewport.clientHeight : 0,
                    pageInset: pageInset,
                    sectionGap: sectionGap,
                    sections: measuredSections,
                }).map(function (page) {
                    return {
                        pageIndex: page.pageIndex,
                        sections: page.sections.map(function (section) {
                            return {
                                sectionKey: section.sectionKey,
                                sectionHeader: section.sectionHeader,
                                continued: section.continued,
                                rows: section.rows.map(function (row) {
                                    return row.payload;
                                }),
                            };
                        }),
                    };
                });

                if (this.currentPageIndex >= this.pages.length) {
                    this.currentPageIndex = 0;
                }

                this.startPageTimer();

                this.$nextTick(function () {
                    this.applyAutoFit();
                });
            });
        },

        getAutoFitKey(text, width, maxLines) {
            return String(text || '') + '|' + String(width || 0) + '|' + String(maxLines || 0);
        },

        getSpanAvailableWidth(spanEl) {
            if (!spanEl || !spanEl.parentElement) {
                return 0;
            }

            var parent = spanEl.parentElement;
            var styles = window.getComputedStyle(parent);
            var paddingLeft = parseFloat(styles.paddingLeft) || 0;
            var paddingRight = parseFloat(styles.paddingRight) || 0;
            var borderLeft = parseFloat(styles.borderLeftWidth) || 0;
            var borderRight = parseFloat(styles.borderRightWidth) || 0;
            var width = parent.clientWidth - paddingLeft - paddingRight - borderLeft - borderRight;

            return width > 0 ? width : 0;
        },

        setSpanFontSize(spanEl, fontSize) {
            if (!spanEl) {
                return;
            }

            spanEl.style.fontSize = fontSize + 'px';
            spanEl.style.lineHeight = AUTO_FIT_LINE_HEIGHT;
        },

        getSpanBaselineFontSize(spanEl) {
            if (!spanEl) {
                return 0;
            }
            // Prefer computed font-size before we override it.
            // If we already set an inline font-size, temporarily clear it to read baseline.
            var originalInline = spanEl.style.fontSize;
            if (originalInline) {
                spanEl.style.fontSize = '';
            }
            var styles = window.getComputedStyle(spanEl);
            var baseline = parseFloat(styles.fontSize) || 0;
            if (originalInline) {
                spanEl.style.fontSize = originalInline;
            }
            return baseline;
        },

        isSpanFitsMaxLines(spanEl, maxLines) {
            if (!spanEl) {
                return true;
            }

            var styles = window.getComputedStyle(spanEl);
            var fontSize = parseFloat(styles.fontSize) || 0;
            var lineHeightValue = styles.lineHeight;
            var lineHeight;

            if (!lineHeightValue || lineHeightValue === 'normal') {
                lineHeight = fontSize * AUTO_FIT_LINE_HEIGHT;
            } else if (lineHeightValue.indexOf('px') > -1) {
                lineHeight = parseFloat(lineHeightValue) || (fontSize * AUTO_FIT_LINE_HEIGHT);
            } else {
                lineHeight = fontSize * AUTO_FIT_LINE_HEIGHT;
            }

            if (!lineHeight) {
                return true;
            }

            var maxHeight = lineHeight * maxLines + 0.5;
            return spanEl.scrollHeight <= maxHeight;
        },

        fitSpanFontSize(spanEl, options) {
            var maxLines = options && options.maxLines ? options.maxLines : MAX_LINES;
            var minFontSize = options && options.minFontSize ? options.minFontSize : AUTO_FIT_MIN_FONT_SIZE;
            var baselineFontSize = this.getSpanBaselineFontSize(spanEl);
            var maxFontSize = options && options.maxFontSize ? options.maxFontSize : baselineFontSize;
            var text = spanEl ? (spanEl.textContent || '') : '';
            var width = this.getSpanAvailableWidth(spanEl);
            var cacheKey = this.getAutoFitKey(text, width, maxLines);
            var cached = this.autoFitCache[cacheKey];

            if (cached) {
                this.setSpanFontSize(spanEl, cached);
                return cached;
            }

            if (!spanEl || !text || !width) {
                return null;
            }

            if (!maxFontSize || maxFontSize < minFontSize) {
                maxFontSize = minFontSize;
            }

            // If baseline already fits, keep it (no unification).
            this.setSpanFontSize(spanEl, maxFontSize);
            if (this.isSpanFitsMaxLines(spanEl, maxLines)) {
                this.autoFitCache[cacheKey] = maxFontSize;
                return maxFontSize;
            }

            var lo = minFontSize;
            var hi = Math.floor(maxFontSize);
            var best = minFontSize;

            while (lo <= hi) {
                var mid = Math.floor((lo + hi) / 2);
                this.setSpanFontSize(spanEl, mid);

                if (this.isSpanFitsMaxLines(spanEl, maxLines)) {
                    best = mid;
                    lo = mid + 1;
                } else {
                    hi = mid - 1;
                }
            }

            this.autoFitCache[cacheKey] = best;
            this.setSpanFontSize(spanEl, best);
            return best;
        },

        applyAutoFit() {
            if (!this.$el) {
                return;
            }

            var selectors = [
                '.meal-list-wrap .meal-content.col-rice span',
                '.meal-list-wrap .meal-content.col-meat span',
                '.meal-list-wrap .meal-content.col-veg span',
                '.measure-layer .meal-content.col-rice span',
                '.measure-layer .meal-content.col-meat span',
                '.measure-layer .meal-content.col-veg span',
            ];

            var spans = this.$el.querySelectorAll(selectors.join(','));
            Array.prototype.forEach.call(spans, function (spanEl) {
                this.fitSpanFontSize(spanEl, { maxLines: MAX_LINES });
            }, this);
        },
    },
};
</script>

<style lang="scss" scoped>
$fontColor: #0A7B23;
$borderColor: #59C06F;

.wrap {
    position: relative;
    height: vh(785);
    background: #b2f5c2;
    overflow: hidden;
    border-radius: 3px;
    font-weight: bold;
}

.meal-list-wrap {
    padding: vh(12) vh(18) vh(58);
}

.meal-list {
    margin-bottom: vh(10);
}

.meal-block {
    border: 1px solid $borderColor;
    color: $fontColor;
    border-radius: vh(10);
    overflow: hidden;
}

.meal-item {
    display: flex;
    align-items: stretch;
    border-bottom: 1px solid $borderColor;
}

.meal-item:last-child {
    border-bottom: 0;
}

.meal-head {
    background: #8EDE9F;
}

.meal-title,
.meal-content {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: vh(10) vh(12);
    box-sizing: border-box;
    word-break: break-all;
    border-right: 1px solid $borderColor;
}

.meal-content.col-rice span,
.meal-content.col-meat span,
.meal-content.col-veg span {
    display: block;
    width: 100%;
    white-space: normal;
    overflow-wrap: break-word;
    word-break: break-word;
    line-height: 1.15;
}

.meal-title:last-child,
.meal-content:last-child {
    border-right: 0;
}

.col-week { width: 18%; }
.col-rice { width: 16%; }
.col-meat { width: 25%; }
.col-veg { width: 16%; }

.meal-content.col-week {
    flex-direction: column;
    gap: vh(2);

    .week-date {
        display: block;
        max-width: 100%;
        font-size: vh(16);
        line-height: 1.2;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    .week-label {
        display: block;
        max-width: 100%;
        font-size: vh(14);
        line-height: 1.1;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }
}

.measure-layer {
    position: absolute;
    left: -9999px;
    top: 0;
    width: 100%;
    height: auto;
    opacity: 0;
    pointer-events: none;
}

.page-turn-control-position {
    position: absolute;
    left: 50%;
    bottom: vh(8);
    z-index: 8;
    transform: translateX(-50%);
}

.page-turn-fade-enter-active,
.page-turn-fade-leave-active {
    transition: opacity 0.3s ease;
}

.page-turn-fade-enter,
.page-turn-fade-leave-to {
    opacity: 0;
}
</style>
